// पुरुषवचनम्: the person × number grid. Three persons × two numbers = six cells, each with the subject pronoun and the
// verb form under it; tap a cell for the sentence, its transliteration, meaning and audio. ?root=पढ&tense=bhavishya
// opens a table. Prakrit has two numbers only (no dual); the अन्य पुरुष pronoun can be switched सो / सा / तं.

import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { Num, Person, Tense } from '@/prakrit/lib/grammar/types'
import type { SubjectGender } from '@/prakrit/components/grammar/dhatu/meaning'

import { useEffect, useMemo, useState } from 'react'
import { Crown, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FREE_TENSE } from '@/lib/plans'
import { useSession } from '@/lib/client/session'
import { verbCells } from '@/prakrit/lib/grammar/cells'
import { transliterate } from '@/prakrit/lib/grammar/translit'
import { GENDERS, NUMBERS, PERSONS } from '@/prakrit/lib/grammar/types'
import {
    GENDER_LABEL,
    NUMBER_LABEL,
    PERSON_ENGLISH_NOTE,
    PERSON_LABEL,
    PERSON_SHORT,
    TENSE_LABEL,
    TENSE_SHORT
} from '@/prakrit/lib/grammar/labels'
import { useLemmaStatuses } from '@/hooks/use-statuses'
import { Bi } from '@/prakrit/components/common/bi'
import { PlanNotice } from '@/components/plans/plan-notice'
import { PrakritText } from '@/prakrit/components/common/prakrit-word'
import { AudioButton } from '@/prakrit/components/common/audio-button'
import { DhatuPicture } from '@/prakrit/components/common/dhatu-picture'
import { StatusBadge, reviewBadgeKind } from '@/prakrit/components/common/status-badge'
import { useUrlQuery } from '@/prakrit/components/grammar/labs/use-url-query'
import { LabNav } from '@/prakrit/components/grammar/labs/lab-ui'
import {
    basicTensesOf,
    findRoot,
    isLabTense,
    labRoots
} from '@/prakrit/components/grammar/labs/verb-data'
import { RootCombobox } from '@/prakrit/components/grammar/dhatu/root-combobox'
import { NUMBER_EN, NUMBER_NOTE, PERSON_EN } from '@/prakrit/components/grammar/dhatu/labels'
import { PANEL, PageIntro, PillGroup } from '@/prakrit/components/grammar/dhatu/dhatu-ui'
import {
    cellGloss,
    coreForm,
    sentenceMeaning,
    sentenceOf,
    subjectOf
} from '@/prakrit/components/grammar/dhatu/meaning'

const DEFAULT_ROOT = 'पढ'

const WIDTH = 'mx-auto w-full max-w-5xl px-4 sm:px-6'

const ROW = 'grid grid-cols-[4.75rem_minmax(0,1fr)_minmax(0,1fr)] gap-2 sm:grid-cols-[6.5rem_minmax(0,1fr)_minmax(0,1fr)] sm:gap-3'

const pronounsOf = (p: Person): string => {
    if (p !== 'anya') return `${subjectOf(p, 'eka')} · ${subjectOf(p, 'bahu')}`

    const one = GENDERS.map(g => subjectOf(p, 'eka', g)).join(', ')
    const many = GENDERS.map(g => subjectOf(p, 'bahu', g)).join(', ')

    return `${one} · ${many}`
}

const InfoTile: FC<{ hi: string, en: string, children: ReactNode }> = ({ hi, en, children }): ReactNode => (
    <div className='flex flex-col gap-1 rounded-2xl bg-surface-muted/70 px-4 py-3'>
        <dt className='text-xs text-muted-foreground'>
            <span lang='hi' className='hindi'>{hi}</span>
            <span aria-hidden='true'> · </span>
            <span lang='en'>{en}</span>
        </dt>
        <dd className='flex flex-col gap-0.5'>{children}</dd>
    </div>
)

export const PersonGridLab: FC = (): ReactNode => {
    const roots = labRoots()
    const url = useUrlQuery()
    const [rootId, setRootId] = useState(DEFAULT_ROOT)
    const [tense, setTense] = useState<Tense>('vartaman')
    const [cell, setCell] = useState<{ person: Person, number: Num }>({ person: 'uttam', number: 'eka' })
    const [gender, setGender] = useState<SubjectGender>('pu')
    const { access, loading: sessionLoading } = useSession()
    const tenseLocked = (t: Tense): boolean => !sessionLoading && !access.full && t !== FREE_TENSE

    const urlRoot = url.read('root')
    const urlTense = url.read('tense')

    useEffect(() => {
        if (!url.ready) return

        setRootId(urlRoot && findRoot(urlRoot) ? urlRoot : DEFAULT_ROOT)
        setTense(urlTense && isLabTense(urlTense) ? urlTense : 'vartaman')
    }, [url.ready, urlRoot, urlTense])

    const root = findRoot(rootId) ?? roots[0]
    const tenses = basicTensesOf(root)
    const activeTense = tenses.includes(tense) ? tense : tenses[0]
    const status = useLemmaStatuses('verb_form', root.root)
    const table = root.tables[activeTense]
    const cells = useMemo(() => verbCells(root.root, activeTense, table, status), [root.root, activeTense, table, status])
    const review = PERSONS.every(p => NUMBERS.every(n => cells[p][n].review === 'verified' || cells[p][n].review === 'published')) ? 'verified' : 'needs_review'

    const { person, number } = cell
    const g = person === 'anya' ? gender : 'pu'
    const form = coreForm(root, activeTense, person, number)
    const sentence = sentenceOf(root, activeTense, person, number, g)
    const meaning = sentenceMeaning(root, activeTense, person, number, g)
    const gloss = cellGloss(root, activeTense, person, number, g)
    const firstForm = coreForm(root, activeTense, 'uttam', 'eka')
    const allSame = PERSONS.every(p => NUMBERS.every(n => coreForm(root, activeTense, p, n) === firstForm))
    const thirdPerson = GENDERS.map(gd => subjectOf('anya', number, gd))

    const selectRoot = (next: string) => {
        const r = findRoot(next)

        if (!r) return

        const t = basicTensesOf(r).includes(activeTense) ? activeTense : 'vartaman'

        setRootId(r.root)
        setTense(t)
        url.write({ root: r.root, tense: t })
    }

    const selectTense = (t: Tense) => {
        setTense(t)
        url.write({ root: root.root, tense: t })
    }

    const onGridKey = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return

        const r = PERSONS.indexOf(person)
        const c = NUMBERS.indexOf(number)
        let nr = r
        let nc = c

        if (event.key === 'ArrowDown') nr = Math.min(PERSONS.length - 1, r + 1)
        else if (event.key === 'ArrowUp') nr = Math.max(0, r - 1)
        else if (event.key === 'ArrowRight') nc = Math.min(NUMBERS.length - 1, c + 1)
        else if (event.key === 'ArrowLeft') nc = Math.max(0, c - 1)
        else if (event.key === 'Home') nc = 0
        else nc = NUMBERS.length - 1

        event.preventDefault()

        const next = { person: PERSONS[nr], number: NUMBERS[nc] }

        setCell(next)
        event.currentTarget.querySelector<HTMLElement>(`[data-cell="${next.person}.${next.number}"]`)?.focus()
    }

    return (
        <>
            <PageIntro
                eyebrow='पुरुषवचनम्'
                title={{ en: 'The person × number grid', hi: 'पुरुष और वचन की तालिका' }}
                intro={{
                    hi: 'तीन पुरुष × दो वचन = छह रूप। किसी भी खाने पर टैप कीजिए।',
                    en: 'Three persons × two numbers = six forms. Tap any cell.'
                }}
                kidsIntro={{
                    hi: 'मैं, तुम, वह — एक हो या कई। कोई भी खाना छुओ और सुनो!',
                    en: 'I, you, he or she: one or many. Tap any box and listen!'
                }}
            />

            <div className={cn(WIDTH, 'flex flex-col gap-6')}>
                <ul className='grid gap-3 md:grid-cols-3'>
                    {PERSONS.map(p => (
                        <li key={p} className='flex flex-col gap-1.5 rounded-3xl border border-border bg-card px-5 py-4 shadow-card'>
                            <p className='flex flex-col leading-tight'>
                                <span lang='hi' className='hindi text-lg font-bold'>{PERSON_LABEL[p].hi}</span>
                                <span lang='en' className='text-sm font-medium'>{PERSON_EN[p]}</span>
                            </p>
                            <p lang='pra' className='pra pra-sm'>{pronounsOf(p)}</p>
                            <Bi hi={PERSON_ENGLISH_NOTE[p].hi} en={PERSON_ENGLISH_NOTE[p].en} className='gap-0.5 text-xs leading-relaxed text-muted-foreground' />
                        </li>
                    ))}
                </ul>

                <div className='flex items-start gap-3 rounded-2xl bg-accent px-5 py-3.5 text-sm text-accent-foreground'>
                    <Info className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                    <Bi
                        hi='प्राकृत में केवल दो वचन हैं — एकवचन और बहुवचन। द्विवचन नहीं होता, इसलिए दो लोगों के लिए भी बहुवचन आता है। “अन्य पुरुष” अंग्रेज़ी का third person है और “उत्तम पुरुष” first person।'
                        en='Prakrit has only two numbers, एकवचन and बहुवचन: there is no dual, so two people already take the plural. “अन्य पुरुष” is English third person, and “उत्तम पुरुष” is English first person.'
                        className='gap-1 leading-relaxed'
                        enClassName='text-accent-foreground/85'
                    />
                </div>

                <div className='flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center'>
                    <div className='w-full sm:w-80'>
                        <RootCombobox roots={roots} value={root.root} onChange={selectRoot} showValue />
                    </div>
                    <PillGroup
                        label='काल · Tense'
                        value={activeTense}
                        options={tenses.map(t => ({
                            value: t,
                            title: `${TENSE_LABEL[t].hi} · ${TENSE_LABEL[t].en}`,
                            label: (
                                <span className='inline-flex items-center gap-1'>
                                    <span lang='hi' className='hindi'>{TENSE_SHORT[t].hi}</span>
                                    {tenseLocked(t) && (
                                        <>
                                            <Crown className='size-3.5' aria-hidden='true' />
                                            <span className='sr-only'>Premium</span>
                                        </>
                                    )}
                                </span>
                            )
                        }))}
                        onChange={selectTense}
                    />
                </div>

                {tenseLocked(activeTense) ? <PlanNotice kind='grammar' compact /> : (
                <section aria-labelledby='grid-title' className={cn(PANEL, 'flex flex-col gap-5 p-4 sm:p-6')}>
                    <div className='flex flex-wrap items-center justify-between gap-3'>
                        <div className='flex items-center gap-3'>
                            <DhatuPicture key={root.root} root={root.root} decorative sizes='64px' className='size-14 shrink-0 rounded-xl motion-safe:animate-in motion-safe:fade-in motion-safe:duration-500 sm:size-16' />
                            <h2 id='grid-title' className='flex flex-wrap items-baseline gap-x-2 font-sans text-sm'>
                                <span lang='pra' className='pra pra-sm'>{root.root}</span>
                                <span className='text-muted-foreground'>
                                    (<span lang='hi' className='hindi'>{root.hi}</span>
                                    <span aria-hidden='true'> · </span>
                                    <span lang='en'>{root.en}</span>)
                                </span>
                                <span aria-hidden='true' className='text-muted-foreground'>·</span>
                                <span lang='hi' className='hindi font-semibold'>{TENSE_LABEL[activeTense].hi}</span>
                                <StatusBadge kind={reviewBadgeKind(review)} withEnglish={false} className='self-center' />
                            </h2>
                        </div>
                        <div className='flex items-center gap-2'>
                            <span className='hidden text-xs text-muted-foreground sm:inline'>
                                <span lang='hi' className='hindi'>अन्य पुरुष</span>
                                <span aria-hidden='true'> · </span>
                                <span lang='en'>third person</span>
                            </span>
                            <PillGroup
                                label='अन्य पुरुष का लिंग · Gender of the third person'
                                value={gender}
                                options={GENDERS.map(gd => ({
                                    value: gd,
                                    title: `${subjectOf('anya', 'eka', gd)}: ${GENDER_LABEL[gd].hi} · ${GENDER_LABEL[gd].en}`,
                                    label: <span lang='pra' className='font-prakrit font-bold'>{subjectOf('anya', 'eka', gd)}</span>
                                }))}
                                onChange={next => {
                                    setGender(next)
                                    if (person !== 'anya') setCell({ person: 'anya', number })
                                }}
                            />
                        </div>
                    </div>

                    <div role='grid' aria-label={`${root.root} · ${TENSE_LABEL[activeTense].hi} · पुरुष × वचन`} onKeyDown={onGridKey} className='flex flex-col gap-2 sm:gap-3'>
                        <div role='row' className={ROW}>
                            <span aria-hidden='true' />
                            {NUMBERS.map(n => (
                                <span key={n} role='columnheader' className='flex flex-col items-center text-center leading-tight'>
                                    <span lang='hi' className='hindi text-sm font-semibold'>{NUMBER_LABEL[n].hi}</span>
                                    <span lang='en' className='text-xs text-muted-foreground'>{NUMBER_EN[n]}</span>
                                </span>
                            ))}
                        </div>

                        {PERSONS.map(p => (
                            <div key={p} role='row' className={ROW}>
                                <span role='rowheader' className='flex flex-col items-end justify-center text-right leading-tight'>
                                    <span lang='hi' className='hindi text-sm font-semibold'>{PERSON_SHORT[p].hi}</span>
                                    <span lang='en' className='text-xs text-muted-foreground'>{PERSON_EN[p]}</span>
                                </span>
                                {NUMBERS.map(n => {
                                    const active = p === person && n === number
                                    const pronoun = subjectOf(p, n, p === 'anya' ? gender : 'pu')
                                    const verb = cells[p][n].core[0] ?? ''

                                    return (
                                        <div key={n} role='gridcell' className='flex'>
                                            <button
                                                type='button'
                                                data-cell={`${p}.${n}`}
                                                aria-pressed={active}
                                                aria-label={`${PERSON_LABEL[p].hi} ${NUMBER_LABEL[n].hi}: ${pronoun} ${verb}`}
                                                tabIndex={active ? 0 : -1}
                                                onClick={() => setCell({ person: p, number: n })}
                                                className={cn(
                                                    'flex min-h-[5.25rem] w-full flex-col items-center justify-center gap-0.5 rounded-2xl border px-2 py-3 text-center transition-colors',
                                                    active ? 'border-saffron bg-saffron shadow-sm' : 'border-border bg-card hover:border-saffron/60 hover:bg-saffron/5'
                                                )}
                                            >
                                                <span lang='pra' className={cn('font-prakrit text-[length:var(--pra-lg)] font-bold leading-tight', active ? 'text-primary-foreground' : 'text-prakrit')}>
                                                    {pronoun}
                                                </span>
                                                <span lang='pra' className={cn('font-prakrit text-lg font-semibold leading-snug', active ? 'text-primary-foreground/80' : 'text-terracotta')}>
                                                    {verb || '—'}
                                                </span>
                                            </button>
                                        </div>
                                    )
                                })}
                            </div>
                        ))}
                    </div>

                    <p className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground'>
                        <Bi
                            hi={`क्रिया का रूप लिंग से नहीं बदलता: ${thirdPerson.join(', ')} के साथ एक ही रूप।`}
                            en={`The verb does not change with gender: ${thirdPerson.join(', ')} take the same form.`}
                            layout='inline'
                        />
                        {allSame && (
                            <span className='rounded-full bg-secondary px-2.5 py-1 text-foreground'>
                                <span lang='hi' className='hindi'>इस काल में छहों खानों में एक ही रूप</span>
                                <span aria-hidden='true' className='ex-sep'> · </span>
                                <span lang='en' data-ex='en' className='text-muted-foreground'>one form for all six cells</span>
                            </span>
                        )}
                    </p>

                    {sentence && (
                        <article aria-label='चुना हुआ खाना · The chosen cell' className='flex flex-col gap-4 rounded-3xl border border-border bg-card p-5 shadow-card sm:p-6'>
                            <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
                                <div className='flex min-w-0 flex-col gap-1'>
                                    <p lang='pra' className='pra pra-xl'>
                                        <PrakritText text={sentence} />
                                    </p>
                                    <p lang='pra-Latn' className='text-lg italic text-muted-foreground'>{transliterate(sentence, { punctuation: true })}</p>
                                </div>
                                <AudioButton text={sentence} className='shrink-0' />
                            </div>

                            {meaning ? (
                                <Bi hi={meaning.hi} en={meaning.en} className='gap-0.5 text-base' />
                            ) : (
                                <Bi hi={gloss.hi} en={gloss.en} className='gap-0.5 text-sm text-muted-foreground' />
                            )}

                            <dl className='grid gap-3 sm:grid-cols-3'>
                                <InfoTile hi='पुरुष' en='Person'>
                                    <span lang='hi' className='hindi font-semibold'>{PERSON_LABEL[person].hi}</span>
                                    <Bi hi={PERSON_ENGLISH_NOTE[person].hi} en={PERSON_ENGLISH_NOTE[person].en} className='gap-0.5 text-xs text-muted-foreground' />
                                </InfoTile>
                                <InfoTile hi='वचन' en='Number'>
                                    <span lang='hi' className='hindi font-semibold'>{NUMBER_LABEL[number].hi}</span>
                                    <Bi hi={NUMBER_NOTE[number].hi} en={NUMBER_NOTE[number].en} className='gap-0.5 text-xs text-muted-foreground' />
                                </InfoTile>
                                <InfoTile hi='क्रिया' en='Verb'>
                                    <span lang='pra' className='pra pra-sm'>{form}</span>
                                    <span className='text-xs text-muted-foreground'>
                                        <span lang='hi' className='hindi'>{root.root} ({root.hi}), {TENSE_LABEL[activeTense].hi}</span>
                                        <span aria-hidden='true' className='ex-sep'> · </span>
                                        <span lang='en' data-ex='en'>{root.en}, {TENSE_LABEL[activeTense].en}</span>
                                    </span>
                                </InfoTile>
                            </dl>
                        </article>
                    )}
                </section>
                )}

                <p className='sr-only' aria-live='polite'>{tenseLocked(activeTense) ? '' : sentence}</p>
            </div>

            <div className={WIDTH}>
                <LabNav current='/grammar/purush-vachan' />
                <div className='h-16' aria-hidden='true' />
            </div>
        </>
    )
}
